import {useEffect, useMemo, useRef, useState} from "react";
import {Button, Form, Modal} from "react-bootstrap";
import SelectField from "../../components/SelectField";
import InputField from "../../components/InputField";
import useTranslation from "../../hooks/translations";
import {useApi} from "../../contexts/ApiProvider";
import CurrencyService from "../../services/CurrencyService";
import IncomeTypeService from "../../services/IncomeTypeService";
import IncomeCategoryService from "../../services/IncomeCategoryService";
import DatePickerField from "../../components/DatePickerField";
import IncomeService from "../../services/IncomeService";
import DatesUtils from "../../utils/dates";

export default function IncomeModal({
                                      showModal,
                                      title,
                                      data,
                                      onModalSubmit,
                                      onModalClose
                                    }) {
  const translations = useTranslation();

  const api = useApi();
  const currencyService = useMemo(() => new CurrencyService(api), [api]);
  const incomeTypeService = useMemo(() => new IncomeTypeService(api), [api]);
  const incomeCategoryService = useMemo(() => new IncomeCategoryService(api), [api]);
  const incomeService = useMemo(() => new IncomeService(api), [api]);

  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [selectedCurrencyOption, setSelectedCurrencyOption] = useState("");
  const handleCurrencyOptionChange = (option) => {
    setSelectedCurrencyOption(option.currentTarget.value);
  };

  const [incomeTypeOptions, setIncomeTypeOptions] = useState([]);
  const [selectedIncomeTypeOption, setSelectedIncomeTypeOption] = useState("");
  const handleIncomeTypeOptionChange = (option) => {
    setSelectedIncomeTypeOption(option.currentTarget.value);
  };

  const [incomeCategoryOptions, setIncomeCategoryOptions] = useState([]);
  const [filteredIncomeCategoryOptions, setFilteredIncomeCategoryOptions] = useState([]);
  const [selectedIncomeCategoryOption, setSelectedIncomeCategoryOption] = useState("");
  const handleIncomeCategoryOptionChange = (option) => {
    setSelectedIncomeCategoryOption(option.currentTarget.value);
  };

  const descriptionField = useRef();
  const dateField = useRef();
  const typeField = useRef();
  const categoryField = useRef();
  const amountField = useRef();
  const currencyField = useRef();
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const getCurrencies = async () => {
      const response = await currencyService.getAll();
      const currencyOptions = response.body.map((item) => {
        return {
          value: item.id,
          name: item.name
        };
      });
      setCurrencyOptions(currencyOptions);
    }
    (async () => {
      await getCurrencies();
    })();
  }, [currencyService]);

  useEffect(() => {
    const getIncomeTypes = async () => {
      const response = await incomeTypeService.getAll();
      const typeOptions = response.body.map((item) => {
        return {
          value: item.id,
          name: item.name
        };
      });
      setIncomeTypeOptions(typeOptions);
    }
    (async () => {
      await getIncomeTypes();
    })();
  }, [incomeTypeService]);

  useEffect(() => {
    const getIncomeCategories = async () => {
      const response = await incomeCategoryService.getAll();
      const categoryOptions = response.body.map((item) => {
        return {
          value: item.id,
          name: item.name,
          type: item.type.id
        };
      });
      setIncomeCategoryOptions(categoryOptions);
      setFilteredIncomeCategoryOptions(categoryOptions)
    }
    (async () => {
      await getIncomeCategories();
    })();
  }, [incomeCategoryService]);

  useEffect(() => {
    if (selectedIncomeCategoryOption) {
      let typeToSelect = incomeCategoryOptions.filter((option) => option.value === Number(selectedIncomeCategoryOption))[0]
      if (typeToSelect && typeToSelect.type !== selectedIncomeTypeOption) {
        setSelectedIncomeCategoryOption("")
      }
    }

    if (!selectedIncomeTypeOption) {
      setFilteredIncomeCategoryOptions(incomeCategoryOptions);
      return;
    }

    setFilteredIncomeCategoryOptions(incomeCategoryOptions.filter(
      (option) => option.type === Number(selectedIncomeTypeOption))
    );
  }, [selectedIncomeTypeOption]);

  useEffect(() => {
    let typeToSelect = incomeCategoryOptions.filter((option) => option.value === Number(selectedIncomeCategoryOption))[0]
    if (typeToSelect) {
      setSelectedIncomeTypeOption(typeToSelect.type)
    }
  }, [selectedIncomeCategoryOption]);

  useEffect(() => {
    (async () => {
      if (Object.keys(data).length > 0) {
        descriptionField.current.value = data.description;
        dateField.current.value = data.date;
        amountField.current.value = data.amount;
        setSelectedCurrencyOption(data.currencyId);
        setSelectedIncomeTypeOption(data.typeId);
        setSelectedIncomeCategoryOption(data.categoryId);
      } else {
        amountField.current.focus();
        dateField.current.value = DatesUtils.formatDate(new Date());
        if (currencyOptions.length > 0) {
          setSelectedCurrencyOption(currencyOptions[0].value);
        }
      }
    })();
  }, [data, currencyOptions]);

  const onSubmit = async (event) => {
    event.preventDefault();

    const description = descriptionField.current.value;
    const date = dateField.current.value;
    const type = parseInt(typeField.current.value);
    const category = parseInt(categoryField.current.value);
    const amount = parseFloat(amountField.current.value);
    const currency = parseInt(currencyField.current.value);

    const errors = {};
    if (!date) {
      errors.date = translations.errorDate;
    }
    if (!type) {
      errors.type = translations.errorType;
    }
    if (!category) {
      errors.category = translations.errorCategory;
    }
    if (!amount) {
      errors.amount = translations.errorAmount;
    }
    if (!currency) {
      errors.currency = translations.errorAmount;
    }
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const dateObject = new Date(date);

    const submitData = {
      description: description,
      timestamp: DatesUtils.formatDate(dateObject),
      type_id: type,
      category_id: category,
      amount: amount,
      currency_id: currency,
    }

    if (data.id) {
      const response = await incomeService.patch(data.id, submitData)
      if (!response.ok) {
        return
      }
    } else {
      const response = await incomeService.create(submitData)
      if (!response.ok) {
        return
      }
    }

    onModalSubmit(submitData)
  };

  return (
    <Modal show={showModal} onHide={onModalClose}>
      <Modal.Header closeButton>
        <Modal.Title>{title ? title : translations.income}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={onSubmit} id="incomeForm">
          <div className="mb-3">
            <DatePickerField
              name="inputDate"
              label={translations.date}
              placeholder={translations.enterDate}
              error={formErrors.date}
              fieldRef={dateField}
            />
          </div>
          <div className="mb-3">
            <InputField
              name="inputAmount"
              type="number"
              step=".01"
              inputmode="decimal"
              label={translations.amount}
              placeholder={translations.enterAmount}
              error={formErrors.amount}
              fieldRef={amountField}
            />
          </div>
          <div className="mb-3">
            <SelectField
              name="currency"
              label={translations.currency}
              placeholder={translations.enterCurrency}
              options={currencyOptions}
              selectedOption={selectedCurrencyOption}
              handleChange={handleCurrencyOptionChange}
              error={formErrors.currency}
              fieldRef={currencyField}
            />
          </div>
          <div className="mb-3">
            <InputField
              name="inputDescription"
              label={translations.description}
              placeholder={translations.enterDescription}
              error={formErrors.description}
              fieldRef={descriptionField}
              asTextarea={true}
            />
          </div>
          <div className="mb-3">
            <SelectField
              name="costType"
              label={translations.type}
              placeholder={translations.enterType}
              options={incomeTypeOptions}
              selectedOption={selectedIncomeTypeOption}
              handleChange={handleIncomeTypeOptionChange}
              error={formErrors.type}
              fieldRef={typeField}
            />
          </div>
          <div className="mb-3">
            <SelectField
              name="costCategory"
              label={translations.category}
              placeholder={translations.enterCategory}
              options={filteredIncomeCategoryOptions}
              selectedOption={selectedIncomeCategoryOption}
              handleChange={handleIncomeCategoryOptionChange}
              error={formErrors.category}
              fieldRef={categoryField}
            />
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" type="submit"
                form="incomeForm">{translations.save}</Button>
        <Button variant="secondary"
                onClick={onModalClose}>{translations.close}</Button>
      </Modal.Footer>
    </Modal>
  )
}
